import { Card, Form, Select, Skeleton } from "antd";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import CustomHeader from "../common/Header";
import { SettingContext } from "../../routes";
import { formatDate } from "../../services/util";
import Chart2 from "./Chart2";
import Chart1 from "./Charts1";
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ZAxis,
    Cell,
} from "recharts";

const { Option } = Select;

const yAxisOptions = [
    { key: "hotels", title: "Hotels" },
    { key: "components", title: "Components" },
];
const xAxisOptions = [
    { key: "months", title: "Months" },
    { key: "days", title: "Days" },
    { key: "hotel_name", title: "Hotels" },
    { key: "component_name", title: "Components" },
];

const COLORS = [
    "var(--color-primary)",
    "var(--color-accent)",
    "var(--color-warning)",
    "var(--color-primary-hover)",
    "var(--color-success)",
    "var(--color-error)",
];

const axisStyle = {
    tick: { fill: "var(--color-text-muted)", fontSize: 11 },
    axisLine: { stroke: "var(--color-border)" },
    tickLine: false,
};

const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
        <div
            style={{
                background: "var(--color-bg-control)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
            }}
        >
            {Object.entries(d).map(([k, v]) => (
                <p
                    key={k}
                    style={{
                        margin: "2px 0",
                        color: "var(--color-text-secondary)",
                    }}
                >
                    {k}: <strong style={{ color: "var(--color-text)" }}>{v}</strong>
                </p>
            ))}
        </div>
    );
};

function transformScatterData(rawData) {
    if (!rawData || rawData.length < 2) return { data: [], xKey: "", yKey: "" };
    const [headers, ...rows] = rawData;
    const [xKey, yKey, zKey] = headers;
    const data = rows.map((row) => {
        const obj = { [xKey]: row[0], [yKey]: Number(row[1]) || 0 };
        if (zKey) obj[zKey] = Number(row[2]) || 1;
        return obj;
    });
    return { data, xKey, yKey, zKey };
}

function TicketsByComponents() {
    const [form] = Form.useForm();
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [chartData, setChartData] = useState([]);
    const [hotelSelectionOps, setHotelSelectionOps] = useState([]);
    const [componentSelectionOps, setComponentSelectionOps] = useState([]);
    const [formData, setFormData] = useState({
        staff_id,
        chart_by: xAxisOptions[0].key,
        chart_for: yAxisOptions[0].key,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        hotel_ids: [],
        all_selected: true,
    });

    const {
        status: chartStatus,
        data: chartRes,
        refetch: getChart,
    } = useQuery(
        "ticketByCompenets",
        async () =>
            await HTTP("/analytics/getMonthlyComponentWiseTickets", {
                ...formData,
            }),
        { enabled: false },
    );
    const {
        status: hotelOptionStatus,
        data: hotelOptionRes,
        refetch: getHotelOptions,
    } = useQuery(
        "kiosk-hotels",
        async () =>
            await HTTP("/dashboard/getHotels", {
                staff_id,
                hotel_type: "kiosk",
            }),
        { enabled: false },
    );
    const {
        status: componentStatus,
        data: componentRes,
        refetch: getComponent,
    } = useQuery(
        "hubspot-components",
        async () => await HTTP("/dashboard/getJiraComponents", { staff_id }),
        { enabled: false },
    );

    useEffect(() => {
        if (hotelOptionStatus === "success")
            setHotelSelectionOps(hotelOptionRes.data);
    }, [hotelOptionStatus, hotelOptionRes]);

    useEffect(() => {
        if (componentStatus === "success")
            setComponentSelectionOps(componentRes.data);
    }, [componentStatus, componentRes]);

    useEffect(() => {
        getHotelOptions();
        getChart();
        getComponent();
        form.setFieldsValue({
            chart_by: xAxisOptions[0].key,
            chart_for: yAxisOptions[0].key,
        });
    }, []);

    useEffect(() => {
        getChart();
    }, [formData]);

    useEffect(() => {
        if (chartStatus === "success") setChartData(chartRes.data);
    }, [chartStatus, chartRes]);

    useEffect(() => {
        const [s, e] = selectedDates;
        setFormData((p) => ({
            ...p,
            start_date: formatDate(s),
            end_date: formatDate(e),
        }));
    }, [selectedDates]);

    function handleChartForSelection(event) {
        let updated = { ...formData, chart_for: event, all_selected: true };
        delete updated.component_ids;
        delete updated.hotel_ids;
        setFormData(
            event === "components"
                ? { ...updated, component_ids: [] }
                : { ...updated, hotel_ids: [] },
        );
    }

    function handelChartBySelection(e) {
        setFormData((p) => ({ ...p, chart_by: e }));
    }
    function handleHotelSelection(e) {
        setFormData((p) => ({
            ...p,
            hotel_ids: e,
            all_selected: e.length === 0,
        }));
    }
    function handleComponentSelection(e) {
        setFormData((p) => ({
            ...p,
            component_ids: e,
            all_selected: e.length === 0,
        }));
    }

    const {
        data: scatterData,
        xKey,
        yKey,
        zKey,
    } = transformScatterData(chartData);
    const isReady = chartStatus === "success" && scatterData.length > 0;

    return (
        <Card className="dashboard-container">
            <CustomHeader />

            {/* ── Tickets by Components (Scatter) ── */}
            <div
                style={{
                    background: "var(--color-bg-card)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 12,
                    padding: "20px 24px",
                    marginBottom: 20,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 20,
                    }}
                >
                    <div
                        style={{
                            width: 3,
                            height: 18,
                            borderRadius: 2,
                            background: "var(--gradient-primary)",
                        }}
                    />
                    <span
                        style={{
                            color: "var(--color-text)",
                            fontWeight: 600,
                            fontSize: 15,
                        }}
                    >
                        Tickets By Components
                    </span>
                </div>

                {/* Filters */}
                <div
                    style={{
                        background: "var(--color-bg-control)",
                        border: "1px solid var(--color-border)",
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 20,
                    }}
                >
                    <Form
                        form={form}
                        layout="inline"
                        style={{ gap: 12, flexWrap: "wrap" }}
                    >
                        <Form.Item
                            shouldUpdate={(p, c) => p.chart_for !== c.chart_for}
                            style={{ flex: 1, minWidth: 200 }}
                        >
                            {({ getFieldValue }) =>
                                getFieldValue("chart_for") === "hotels" ? (
                                    <Form.Item
                                        label={
                                            <span style={{ color: "var(--color-text-muted)" }}>
                                                Hotels
                                            </span>
                                        }
                                        name="hotel_ids"
                                        style={{ margin: 0 }}
                                    >
                                        <Select
                                            allowClear
                                            mode="multiple"
                                            onChange={handleHotelSelection}
                                            loading={!hotelSelectionOps.length}
                                            placeholder="All hotels"
                                        >
                                            {hotelSelectionOps.map((e) => (
                                                <Option
                                                    key={e.hotel_id}
                                                    value={e.hotel_id}
                                                >
                                                    {`${e.hotel_id} - ${e.hotel_name}`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                ) : (
                                    <Form.Item
                                        label={
                                            <span style={{ color: "var(--color-text-muted)" }}>
                                                Components
                                            </span>
                                        }
                                        name="component_ids"
                                        style={{ margin: 0 }}
                                    >
                                        <Select
                                            allowClear
                                            mode="multiple"
                                            onChange={handleComponentSelection}
                                            loading={
                                                !componentSelectionOps.length
                                            }
                                            placeholder="All components"
                                        >
                                            {componentSelectionOps.map((c) => (
                                                <Option
                                                    key={c.component_id}
                                                    value={c.component_id}
                                                >
                                                    {c.component_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                )
                            }
                        </Form.Item>
                        <Form.Item
                            shouldUpdate={(p, c) => p.chart_by !== c.chart_by}
                            style={{ flex: 1, minWidth: 160 }}
                        >
                            {({ getFieldValue }) => (
                                <Form.Item
                                    label={
                                        <span style={{ color: "var(--color-text-muted)" }}>
                                            Y-Axis
                                        </span>
                                    }
                                    name="chart_for"
                                    style={{ margin: 0 }}
                                >
                                    <Select onChange={handleChartForSelection}>
                                        {yAxisOptions.map((e) => {
                                            const sel = xAxisOptions.find(
                                                (x) =>
                                                    x.key ===
                                                    getFieldValue("chart_by"),
                                            );
                                            return (
                                                <Option
                                                    key={e.key}
                                                    value={e.key}
                                                    disabled={
                                                        sel?.title === e.title
                                                    }
                                                >
                                                    {e.title}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            )}
                        </Form.Item>
                        <Form.Item
                            shouldUpdate={(p, c) => p.chart_for !== c.chart_for}
                            style={{ flex: 1, minWidth: 160 }}
                        >
                            {({ getFieldValue }) => (
                                <Form.Item
                                    label={
                                        <span style={{ color: "var(--color-text-muted)" }}>
                                            X-Axis
                                        </span>
                                    }
                                    name="chart_by"
                                    style={{ margin: 0 }}
                                >
                                    <Select onChange={handelChartBySelection}>
                                        {xAxisOptions.map((e) => {
                                            const sel = yAxisOptions.find(
                                                (y) =>
                                                    y.key ===
                                                    getFieldValue("chart_for"),
                                            );
                                            return (
                                                <Option
                                                    key={e.key}
                                                    value={e.key}
                                                    disabled={
                                                        sel?.title === e.title
                                                    }
                                                >
                                                    {e.title}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            )}
                        </Form.Item>
                    </Form>
                </div>

                {isReady ? (
                    <ResponsiveContainer width="100%" height={400}>
                        <ScatterChart
                            margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="var(--color-border-light)"
                            />
                            <XAxis dataKey={xKey} name={xKey} {...axisStyle} />
                            <YAxis
                                dataKey={yKey}
                                name={yKey}
                                {...axisStyle}
                                allowDecimals={false}
                            />
                            {zKey && <ZAxis dataKey={zKey} range={[40, 400]} />}
                            <Tooltip
                                content={<CustomTooltip />}
                                cursor={{
                                    strokeDasharray: "3 3",
                                    stroke: "var(--color-bg-spotlight)",
                                }}
                            />
                            <Scatter data={scatterData} name="Tickets">
                                {scatterData.map((_, i) => (
                                    <Cell
                                        key={i}
                                        fill={COLORS[i % COLORS.length]}
                                        fillOpacity={0.8}
                                    />
                                ))}
                            </Scatter>
                        </ScatterChart>
                    </ResponsiveContainer>
                ) : (
                    <Skeleton
                        active
                        title={false}
                        paragraph={{ rows: 8, width: "100%" }}
                    />
                )}
            </div>

            {/* ── Chart1 & Chart2 ── */}
            <div style={{ marginBottom: 20 }}>
                <Chart1 />
            </div>
            <div>
                <Chart2 />
            </div>
        </Card>
    );
}

export { TicketsByComponents };
